import { MIN_SELECT_QUERY_LENGTH } from "@p0security/shared/integrations/notifiers/constants";
import { WebInputChoice } from "@p0security/shared/integrations/notifiers/web-requests/types";
import type { SelectProps } from "@p0security/ui";
import { Select, Spin } from "@p0security/ui";
import debounce from "lodash/debounce";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { NoContentFound } from "./NoContentFound";
import { createOptionGroups } from "./Utils";

export type BaseSelectProps = Omit<
  SelectProps<WebInputChoice | WebInputChoice[]>,
  "children" | "options" | "ref"
>;

export interface DebounceSelectProps extends BaseSelectProps {
  fetchOptions: (search: string) => Promise<WebInputChoice[]>;
  debounceTimeout?: number;
  minCharsForSearch?: number;
}

const DebounceSelect = React.forwardRef(
  (
    {
      fetchOptions,
      debounceTimeout = 500,
      minCharsForSearch = MIN_SELECT_QUERY_LENGTH,
      ...props
    }: DebounceSelectProps,
    ref: React.ForwardedRef<any>
  ) => {
    const [nascent, setNascent] = useState(true);
    const [search, setSearch] = useState("");
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<WebInputChoice[]>([]);
    const fetchRef = useRef(0);

    const debounceFetcher = useMemo(() => {
      const loadOptions = (value: string) => {
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setOptions([]);
        setFetching(true);

        fetchOptions(value)
          .then((newOptions) => {
            if (fetchId !== fetchRef.current) {
              return;
            }
            setOptions(newOptions);
            setNascent(false);
          })
          .catch((err) => {
            if (fetchId !== fetchRef.current) {
              return;
            }
            console.error("Failed to fetch options", err);
            setOptions([]);
            setNascent(false);
          })
          .finally(() => {
            if (fetchId === fetchRef.current) {
              setFetching(false);
            }
          });
      };

      const debounced = debounce(loadOptions, debounceTimeout);

      return (value: string) => {
        setSearch(value);
        if (value.length >= minCharsForSearch) {
          debounced(value);
        } else {
          setOptions([]);
          setNascent(true);
        }
      };
    }, [fetchOptions, debounceTimeout, minCharsForSearch]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        setSearch("");
        if (open) {
          debounceFetcher("");
        }
      },
      [debounceFetcher]
    );

    return (
      <Select
        ref={ref}
        showSearch
        filterOption={false}
        searchValue={search}
        onOpenChange={handleOpenChange}
        onSearch={debounceFetcher}
        allowClear
        notFoundContent={
          search.length < minCharsForSearch ? (
            <div>Type at least {minCharsForSearch} characters to search</div>
          ) : fetching || nascent ? (
            <Spin size="small" />
          ) : (
            <NoContentFound
              description={
                "Your search returned no results. Expand your search or try different keywords."
              }
            />
          )
        }
        {...props}
        options={createOptionGroups(options)}
      />
    );
  }
);

DebounceSelect.displayName = "DebounceSelect";

export const SearchSelect = React.forwardRef(
  (
    props: {
      fetchOptions: (search: string) => Promise<WebInputChoice[]>;
      minCharsForSearch?: number;
    } & BaseSelectProps,
    ref: React.ForwardedRef<SelectProps<WebInputChoice | WebInputChoice[]>>
  ) => {
    return <DebounceSelect ref={ref} className="w-full" {...props} />;
  }
);
SearchSelect.displayName = "SearchSelect";
